import { useQuery } from "@tanstack/react-query";
import { Banknote, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtDateUk } from "@/lib/utils";

interface XeroSummary {
  ytdStart: string;
  since12mo: string;
  today: string;
  totals: {
    revenue_ytd_gross: number;
    revenue_ytd_net: number;
    expenses_ytd_gross: number;
    expenses_ytd_net: number;
    gross_margin_ytd: number;
    gross_margin_pct: number | null;
    outstanding_ar: number;
    outstanding_ap: number;
    unpaid_invoice_count: number;
    active_customers_ytd: number;
  };
  monthly: Array<{
    month: string;
    revenue_gross: number;
    expenses_gross: number;
    gross_margin: number;
  }>;
  topCustomers: Array<{
    contact_name: string;
    invoices: number;
    invoiced_gross: number;
    outstanding: number;
  }>;
  arAgeing: Array<{ bucket: string; invoices: number; outstanding: number }>;
  recentUnpaid: Array<{
    invoice_number: string;
    contact_name: string;
    invoice_date: string;
    due_date: string;
    total_gross: number;
    amount_due: number;
    days_overdue: number;
  }>;
  dataGaps: string[];
}

function fmtGbp(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `£${Math.round(n).toLocaleString()}`;
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return Math.round(n).toLocaleString();
}

export default function XeroFinance() {
  const { data, isLoading, error } = useQuery<XeroSummary>({
    queryKey: ["xero-summary"],
    queryFn: async () => {
      const res = await fetch("/api/dashboards/xero", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading)
    return <div className="p-6 text-muted-foreground">Loading Xero financials from BigQuery…</div>;
  if (error || !data)
    return (
      <div className="p-6 text-red-600">
        Failed to load: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );

  const { totals, monthly, topCustomers, arAgeing, recentUnpaid, dataGaps, ytdStart, today } = data;

  const maxMonthlyRev = Math.max(1, ...monthly.map((m) => m.revenue_gross));

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Banknote className="h-6 w-6" /> Xero financials
        </h1>
        <p className="text-sm text-muted-foreground">
          Live invoiced totals from <code>keystone_finance.invoices</code>.
          YTD = {fmtDateUk(ytdStart)} → {fmtDateUk(today)}.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Revenue YTD"
          value={fmtGbp(totals.revenue_ytd_gross)}
          sub={`net ${fmtGbp(totals.revenue_ytd_net)}`}
        />
        <Kpi
          label="Expenses YTD"
          value={fmtGbp(totals.expenses_ytd_gross)}
          sub={`net ${fmtGbp(totals.expenses_ytd_net)}`}
        />
        <Kpi
          label="Gross margin YTD"
          value={fmtGbp(totals.gross_margin_ytd)}
          sub={
            totals.gross_margin_pct !== null
              ? `${totals.gross_margin_pct}% of revenue`
              : "—"
          }
        />
        <Kpi
          label="Outstanding AR"
          value={fmtGbp(totals.outstanding_ar)}
          sub={`${fmtInt(totals.unpaid_invoice_count)} unpaid invoices`}
        />
        <Kpi label="Outstanding AP" value={fmtGbp(totals.outstanding_ap)} />
        <Kpi
          label="Active customers YTD"
          value={fmtInt(totals.active_customers_ytd)}
          sub="distinct contacts billed"
        />
      </div>

      {/* Monthly revenue vs expenses */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly revenue, expenses & gross margin (last 12 months)</CardTitle>
          <p className="text-xs text-muted-foreground">
            ACCREC = sales · ACCPAY = purchases · PAID + AUTHORISED only · gross (incl. tax).
          </p>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Month</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Expenses</th>
                <th className="text-right">Gross margin</th>
                <th className="pl-3">Revenue trend</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m) => {
                const pct = (m.revenue_gross / maxMonthlyRev) * 100;
                return (
                  <tr key={m.month} className="border-t">
                    <td className="py-1.5 pr-2">{m.month}</td>
                    <td className="text-right">{fmtGbp(m.revenue_gross)}</td>
                    <td className="text-right text-muted-foreground">
                      {fmtGbp(m.expenses_gross)}
                    </td>
                    <td
                      className={`text-right font-medium ${
                        m.gross_margin < 0 ? "text-red-600" : ""
                      }`}
                    >
                      {fmtGbp(m.gross_margin)}
                    </td>
                    <td className="pl-3 w-1/3">
                      <div className="h-2 bg-muted rounded-sm">
                        <div
                          className="h-2 bg-primary rounded-sm"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* AR ageing + top customers side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>AR ageing — outstanding ACCREC</CardTitle>
            <p className="text-xs text-muted-foreground">
              Authorised invoices with amount_due &gt; 0, bucketed by days past due_date.
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Bucket</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {arAgeing.map((a) => (
                  <tr key={a.bucket} className="border-t">
                    <td className="py-1.5 pr-2">{a.bucket}</td>
                    <td className="text-right">{fmtInt(a.invoices)}</td>
                    <td
                      className={`text-right font-medium ${
                        a.bucket === "90+ days" ? "text-red-600" : ""
                      }`}
                    >
                      {fmtGbp(a.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top customers (last 12 months)</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Customer</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Invoiced</th>
                  <th className="text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c) => (
                  <tr key={c.contact_name} className="border-t">
                    <td className="py-1.5 pr-2 max-w-[280px] truncate">
                      {c.contact_name}
                    </td>
                    <td className="text-right">{fmtInt(c.invoices)}</td>
                    <td className="text-right font-medium">{fmtGbp(c.invoiced_gross)}</td>
                    <td
                      className={`text-right ${
                        c.outstanding > 0 ? "text-amber-600" : "text-muted-foreground"
                      }`}
                    >
                      {fmtGbp(c.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Unpaid invoices */}
      <Card>
        <CardHeader>
          <CardTitle>Unpaid customer invoices (oldest first)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Invoice #</th>
                <th>Customer</th>
                <th>Invoice date</th>
                <th>Due date</th>
                <th className="text-right">Total</th>
                <th className="text-right">Outstanding</th>
                <th className="text-right">Days overdue</th>
              </tr>
            </thead>
            <tbody>
              {recentUnpaid.map((inv) => (
                <tr key={inv.invoice_number} className="border-t">
                  <td className="py-1.5 pr-2">{inv.invoice_number}</td>
                  <td className="pr-2 max-w-[220px] truncate">{inv.contact_name}</td>
                  <td>{fmtDateUk(inv.invoice_date)}</td>
                  <td>{fmtDateUk(inv.due_date)}</td>
                  <td className="text-right">{fmtGbp(inv.total_gross)}</td>
                  <td className="text-right font-medium">{fmtGbp(inv.amount_due)}</td>
                  <td
                    className={`text-right ${
                      inv.days_overdue > 30 ? "text-red-600" : "text-muted-foreground"
                    }`}
                  >
                    {inv.days_overdue > 0 ? inv.days_overdue : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {dataGaps.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> Notes &amp; data gaps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              {dataGaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="text-xl font-bold">{value}</div>
        {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}
